// ADR-0022 — Passo do ROSTO dentro da cerimônia de primeiro acesso.
//
// Irmão do `FaceCaptureWidget` do web-admin, com UMA diferença que é o ponto inteiro:
// **este componente NÃO envia nada.** Ele captura e devolve o Blob ao pai, que o manda
// junto com a senha e o TOTP num único POST. O do admin sobe a foto na hora, porque lá o
// cadastro é um ato isolado; aqui a gravação tem de ser atômica com a senha, senão uma
// falha no meio deixa a conta ativada e sem template — estado que não se recupera (a
// cerimônia devolve 409, o `POST /users` devolve 409 `email_exists`, e o `credential-reset`
// exige dois admin_ti e entrega a ELES o token do dono).
//
// Ordem das telas na cerimônia: ROSTO primeiro, senha e TOTP depois. É deliberado: o
// código do autenticador vive 30 s, e o `/first-access` tranca a conta em 5 erros de TOTP
// em 15 min. Pedir o código antes de a pessoa lidar com permissão de câmera e
// enquadramento faria o código expirar enquanto ela se ajeita — e duas ou três repetições
// disso trancam a conta na própria cerimônia.
//
// LAYOUT: mesmo envelope do `LoginPage` e do `FirstAccessForm` — `maxWidth: 440` centrado
// + `<Card>`. Sem isso a tela nasce colada à esquerda e destoa do passo seguinte, que é
// literalmente a próxima coisa que a pessoa vê.
//
// A foto NÃO é armazenada em lugar nenhum: vira um template matemático cifrado no
// servidor. A câmera é desligada assim que a foto é capturada, e no unmount.

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card } from '@aions/ui-primitives';

export interface FirstAccessFaceStepProps {
  /** Recebe a foto capturada. O envio é do pai, no POST único da cerimônia. */
  readonly onCaptured: (foto: Blob) => void;
  /**
   * Erro vindo do SERVIDOR depois do envio — a foto foi recusada pelo serviço de biometria
   * e o pai devolveu a pessoa a este passo. Precisa ser exibido AQUI: no formulário ele caía
   * na mensagem genérica de token inválido, que manda pedir link novo quando o link está bom.
   */
  readonly erroExterno?: string | null;
}

type Fase = 'idle' | 'camera' | 'captured' | 'error';

// Moldura do vídeo/preview — proporção fixa para a tela não "pular" ao trocar de fase.
//
// ⚠️ SEM `display` aqui, de propósito. A primeira versão trazia `display: 'block'`, e estilo
// inline VENCE o atributo `hidden` (que é só `display:none` da folha do agente) — resultado:
// o <video> aparecia vazio mesmo com `hidden`, e a tela mostrava DUAS molduras empilhadas.
// Cada elemento decide o próprio `display` pela fase, abaixo.
const MOLDURA: React.CSSProperties = {
  width: '100%',
  // 16/10 e nao 4/3: com 4/3 o card passava de 560px e o botao principal caia abaixo da
  // dobra em tela de notebook — a pessoa via a explicacao e nao via o que fazer.
  aspectRatio: '16 / 10',
  objectFit: 'cover',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--surface-inset)',
};

export function FirstAccessFaceStep({
  onCaptured,
  erroExterno = null,
}: FirstAccessFaceStepProps): JSX.Element {
  const { t } = useTranslation();
  const [fase, setFase] = useState<Fase>('idle');
  const [erro, setErro] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const pararCamera = useCallback((): void => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      pararCamera();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [pararCamera, previewUrl]);

  const abrirCamera = useCallback(async (): Promise<void> => {
    setErro(null);
    // 🔴 CELULAR — a falha mais provável aqui NÃO é permissão de câmera.
    // O link chega por mensagem e a pessoa toca nele DENTRO do aplicativo. O webview do
    // WhatsApp/Instagram/Gmail costuma não expor `mediaDevices` de todo. Sem esta checagem
    // a chamada cai no `catch` abaixo e a copy manda "verifique a permissão do navegador" —
    // só que não há permissão a verificar: a API não existe naquele contexto. A pessoa fica
    // sem saída, com um link válido na mão e uma instrução impossível de cumprir.
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      setErro(t('first_access.rosto.erro_camera_webview'));
      setFase('error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setFase('camera');
    } catch (e) {
      // Rede corporativa e política de navegador negam `getUserMedia` com frequência.
      // A mensagem tem de dizer que o LINK NÃO FOI GASTO, senão a pessoa acha que
      // perdeu a única chance e chama a TI — que é o caminho que o ADR-0022 evita.
      //
      // `NotAllowedError` é negativa de PERMISSÃO e tem saída própria (liberar nas
      // configurações do site). Os demais — NotFound, NotReadable (câmera ocupada por
      // outro app, comum no celular), Overconstrained — são de dispositivo e ficam com
      // a mensagem geral. Mandar "verifique a permissão" para câmera OCUPADA faria a
      // pessoa mexer numa configuração que já está certa.
      const negada = e instanceof Error && e.name === 'NotAllowedError';
      setErro(
        t(negada ? 'first_access.rosto.erro_camera_negada' : 'first_access.rosto.erro_camera'),
      );
      setFase('error');
    }
  }, [t]);

  const capturar = useCallback((): void => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // 🔴 `video.videoWidth || 640` era um FALLBACK PLAUSÍVEL, e por isso perigoso: com os
    // metadados do vídeo ainda não carregados `videoWidth` é 0, e o fallback desenhava um
    // quadro PRETO de 640x480 que seguia adiante como se fosse a foto. O template facial
    // sairia de uma imagem vazia — falha silenciosa no lugar mais caro do sistema. Agora a
    // ausência é DITA, e a fase continua em `camera`: é só tocar de novo num segundo.
    const wOrig = video.videoWidth;
    const hOrig = video.videoHeight;
    if (wOrig === 0 || hOrig === 0) {
      setErro(t('first_access.rosto.erro_camera_carregando'));
      return;
    }

    // 📱 TETO DE RESOLUÇÃO — a causa provável do "não foi possível capturar" em celular.
    // A frontal de um aparelho atual entrega ~12 MP. O iOS Safari tem limite de ÁREA de
    // canvas e, acima dele, `toBlob` devolve null ou o canvas sai em branco. Somado a isso,
    // um JPEG de 12 MP a 0.92 são vários MB subindo por rede móvel. 1280px no maior lado é
    // de sobra para o template, e o `Math.min(1, …)` garante que isto só REDUZ: numa webcam
    // de 640x480 a escala é 1 e nada muda.
    const TETO_PX = 1280;
    const escala = Math.min(1, TETO_PX / Math.max(wOrig, hOrig));
    const w = Math.round(wOrig * escala);
    const h = Math.round(hOrig * escala);
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setErro(t('first_access.rosto.erro_captura'));
      setFase('error');
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setErro(t('first_access.rosto.erro_captura'));
          setFase('error');
          return;
        }
        blobRef.current = blob;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        pararCamera();
        setFase('captured');
      },
      'image/jpeg',
      0.92,
    );
  }, [t, previewUrl, pararCamera]);

  const refazer = useCallback((): void => {
    blobRef.current = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFase('idle');
  }, [previewUrl]);

  const confirmar = useCallback((): void => {
    if (blobRef.current) onCaptured(blobRef.current);
  }, [onCaptured]);

  return (
    <div style={{ maxWidth: 440, margin: 'var(--space-6, 24px) auto' }}>
      <Card padding="lg" data-testid="first-access-rosto">
        <p
          style={{
            color: 'var(--text-tertiary)',
            fontSize: '0.8125rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          {t('first_access.rosto.passo')}
        </p>
        <h2 className="cf-card__title" style={{ marginBottom: 'var(--space-2, 8px)' }}>
          {t('first_access.rosto.title')}
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4, 16px)' }}>
          {t('first_access.rosto.intro')}
        </p>

        {(erro ?? erroExterno) !== null && (
          <div style={{ marginBottom: 'var(--space-4, 16px)' }}>
            <Alert variant="error" data-testid="rosto-erro">
              {erro ?? erroExterno}
            </Alert>
          </div>
        )}

        {/* O <video> fica SEMPRE montado: atribuir `srcObject` a um elemento que ainda não
            está no DOM é a forma mais comum de a câmera "abrir e não aparecer". */}
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ ...MOLDURA, display: fase === 'camera' ? 'block' : 'none' }}
          aria-label={t('first_access.rosto.video_alt')}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {fase === 'captured' && previewUrl !== null && (
          <img
            src={previewUrl}
            alt={t('first_access.rosto.preview_alt')}
            style={{ ...MOLDURA, display: 'block' }}
          />
        )}

        {(fase === 'idle' || fase === 'error') && (
          <div
            style={{
              ...MOLDURA,
              display: 'grid',
              placeItems: 'center',
              color: 'var(--text-tertiary)',
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: 'var(--space-4, 16px)',
            }}
          >
            {t('first_access.rosto.placeholder')}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2, 8px)',
            marginTop: 'var(--space-4, 16px)',
          }}
        >
          {(fase === 'idle' || fase === 'error') && (
            <Button block onClick={() => void abrirCamera()}>
              {t('first_access.rosto.abrir_camera')}
            </Button>
          )}
          {fase === 'camera' && (
            <Button block onClick={capturar} data-testid="rosto-capturar">
              {t('first_access.rosto.capturar')}
            </Button>
          )}
          {fase === 'captured' && (
            <>
              <Button block onClick={confirmar} data-testid="rosto-continuar">
                {t('first_access.rosto.continuar')}
              </Button>
              <Button block variant="ghost" onClick={refazer}>
                {t('first_access.rosto.refazer')}
              </Button>
            </>
          )}
        </div>

        <p
          style={{
            color: 'var(--text-tertiary)',
            fontSize: '0.75rem',
            marginTop: 'var(--space-4, 16px)',
            marginBottom: 0,
          }}
        >
          {t('first_access.rosto.privacidade')}
        </p>
      </Card>
    </div>
  );
}
